import { currencyHelper } from '../../frontend/utils/currency.js'

const DOMESTIC_SHIPPING_FEES = {
  convenience_store: { TWD: 60, HKD: 20, CNY: 20, JPY: 333, USD: 2 },
  home_delivery:     { TWD: 135, HKD: 45, CNY: 45, JPY: 750, USD: 5 }
}

export function ShippingSelector() {
  return {
    open: true,
    shippingMethod: "",
    shippingProvider: "",
    storeInfo: null,
    isLoadingStore: false,
    shippingRates: null,
    countryCode: null,
    selectedItems: null,
    availableCountries: [],
    errorMessage: "",
    currencyCode: 'TWD',
    currencySymbol: 'NT$',
    i18n: {
      countryNotAvailable: '',
      pleaseSelectAddress: '',
      domesticNotAvailable: '',
      overseasNotAvailable: ''
    },

    // 初始化
    async init() {
      // 載入幣別資訊
      const currencyInfo = await currencyHelper.loadCurrencyInfo()
      this.currencyCode = currencyInfo.currencyCode
      this.currencySymbol = currencyInfo.currencySymbol

      // 從表單獲取現有值
      const methodInput = this.$el.querySelector('select[name="order[order_recipient_attributes][shipping_method]"]')
      if (methodInput && methodInput.value) {
        this.shippingMethod = methodInput.value
        // 如果已有選擇的物流方式，立即觸發運費更新
        this.$nextTick(() => {
          this.updateShippingFee()

          // 也觸發物流方式變更事件
          this.$dispatch('shipping-method-changed', {
            method: this.shippingMethod,
            shippingFee: this.getShippingFee()
          })
        })
      }

      // 從表單獲取現有的超商選擇
      const providerInput = this.$el.querySelector('select[name="order[order_recipient_attributes][shipping_provider]"]')
      if (providerInput && providerInput.value) {
        this.shippingProvider = providerInput.value
      }

      // 如果已有門市資訊，恢復顯示
      const storeIdInput = this.$el.querySelector('input[name="order[order_recipient_attributes][store_id]"]')
      if (storeIdInput && storeIdInput.value) {
        this.storeInfo = {
          id: storeIdInput.value,
          name: this.$el.querySelector('input[name="order[order_recipient_attributes][store_name]"]')?.value || '',
          address: this.$el.querySelector('input[name="order[order_recipient_attributes][store_address]"]')?.value || ''
        }
      }

      // 監聽門市選擇回傳訊息
      this.setupMessageListener();
      this.shippingRates = JSON.parse(this.$el.dataset.shippingRates);
      this.selectedItems = JSON.parse(this.$el.dataset.selectedItems);
      this.availableCountries = JSON.parse(this.$el.dataset.availableCountries || '[]');

      this.i18n.countryNotAvailable = this.$el.dataset.i18nCountryNotAvailable || 'Sorry, this order cannot be shipped to {country}';
      this.i18n.pleaseSelectAddress = this.$el.dataset.i18nPleaseSelectAddress || 'Please select an address first';
      this.i18n.domesticNotAvailable = this.$el.dataset.i18nDomesticNotAvailable || '此商品不支援台灣配送，請選擇其他配送方式';
      this.i18n.overseasNotAvailable = this.$el.dataset.i18nOverseasNotAvailable || '此商品不支援海外宅配，請選擇其他配送方式';
    },

    // 設定訊息監聽器
    setupMessageListener() {
      window.addEventListener('message', (event) => {
        // 安全性檢查：確保訊息來自我們的網站
        const allowedOrigins = [
          window.location.origin,
          'http://localhost:3000',
          'http://127.0.0.1:3000'
        ]

        // 開發環境允許本地來源
        if (!allowedOrigins.includes(event.origin)) {
          console.warn('Rejected message from untrusted origin:', event.origin)
          return
        }

        // 檢查訊息類型
        if (event.data && event.data.type === 'store_selected') {
          // 處理 7-11 門市選擇
          this.handleStoreSelected(event.data.data)
        } else if (event.data && event.data.type === 'famimart_store_selected') {
          // 處理全家門市選擇
          this.handleStoreSelected(event.data.data)
        }
      })
    },

    // 顯示狀態控制
    get showStoreSelector() {
      return this.isConvenienceStore()
    },

    get showAddressFields() {
      return this.shippingMethod === 'home_delivery'
    },

    get showOverseasAddressFields() {
      return this.shippingMethod === 'overseas_delivery'
    },

    get addressRequired() {
      return this.shippingMethod === 'home_delivery' || this.shippingMethod === 'overseas_delivery'
    },

    // 判斷是否為超商店取（包含 7-11 和全家）
    isConvenienceStore() {
      return this.shippingMethod === 'convenience_store_711' ||
        this.shippingMethod === 'convenience_store_famimart' ||
        this.shippingMethod === 'convenience_store' // 向後相容
    },

    // 將複合運送方式值轉換為標準值（用於提交表單）
    get normalizedShippingMethod() {
      if (this.shippingMethod === 'convenience_store_711' ||
        this.shippingMethod === 'convenience_store_famimart') {
        return 'convenience_store'
      }
      return this.shippingMethod
    },

    // 處理運送方式變更
    handleShippingMethodChange() {
      // 海外宅配不能使用免運折價券
      const el = document.querySelector('.coupon-discount-type');
      if (el.dataset.discountType == "free_shipping" && this.shippingMethod == "overseas_delivery") {
        this.shippingMethod = ""

        this.$dispatch('shipping-method-changed', {
          method: this.shippingMethod,
          shippingFee: this.getShippingFee()
        })

        this.errorMessage = "海外宅配無法使用免運券"
        setTimeout(() => {
          this.errorMessage = ""
        }, 1500)
        return
      }

      const prevProvider = this.shippingProvider

      if (this.shippingMethod === 'convenience_store_711') {
        this.shippingProvider = '7-11'
      } else if (this.shippingMethod === 'convenience_store_famimart') {
        this.shippingProvider = 'famimart'
      } else if (this.shippingMethod === 'home_delivery') {
        this.shippingProvider = 'UBG-ECAN'
      }

      // 重置不需要的資料
      if (!this.isConvenienceStore()) {
        this.storeInfo = null
        this.shippingProvider = ""
        this.clearStoreFields()
      } else if (prevProvider && prevProvider !== this.shippingProvider) {
        // 在不同超商之間切換時，清除舊的門市資訊
        this.storeInfo = null
        this.clearStoreFields()
      }

      // 計算並顯示運費
      this.updateShippingFee()

      // 觸發自定義事件供其他元件使用
      this.$dispatch('shipping-method-changed', {
        method: this.shippingMethod,
        shippingFee: this.getShippingFee()
      })
    },

    // 取得運費（根據使用者幣別直接使用商家設定的運費）
    getShippingFee() {
      this.countryCode = this.$store.frontend.countryCode;

      switch (this.shippingMethod) {
        case 'convenience_store':
        case 'convenience_store_711':
        case 'convenience_store_famimart':
          return DOMESTIC_SHIPPING_FEES.convenience_store[this.currencyCode] ?? DOMESTIC_SHIPPING_FEES.convenience_store.TWD;
        case 'home_delivery':
          return DOMESTIC_SHIPPING_FEES.home_delivery[this.currencyCode] ?? DOMESTIC_SHIPPING_FEES.home_delivery.TWD;
        case 'overseas_delivery': {
          // 將同一商品id但不同銷售組合的商品視為同一商品去計算運費
          const groupedItems = this.selectedItems.reduce((acc, item) => {
            if (acc[item.product_id]) {
              acc[item.product_id].quantity += item.quantity;
            } else {
              acc[item.product_id] = { ...item };
            }
            return acc;
          }, {});

          let totalFee = 0;
          const currency = this.currencyCode;

          for (const productId in groupedItems) {
            const item = groupedItems[productId];
            const rate = this.shippingRates.find(r =>
              r.country_code === this.countryCode && r.product_id === item.product_id
            );

            if (rate && rate.fees && rate.fees[currency]) {
              const baseFee = Number(rate.fees[currency].base_fee) || 0;
              const additionalFee = Number(rate.fees[currency].additional_fee) || 0;
              totalFee += baseFee + (item.quantity - 1) * additionalFee;
            } else if (rate && rate.fees && rate.fees['TWD']) {
              // fallback 到 TWD
              const baseFee = Number(rate.fees['TWD'].base_fee) || 0;
              const additionalFee = Number(rate.fees['TWD'].additional_fee) || 0;
              totalFee += baseFee + (item.quantity - 1) * additionalFee;
            }
          }

          return totalFee;
        }

        default:
          return 0;
      }
    },

    // 顯示運費
    displayShippingFee() {
      const fee = this.getShippingFee()
      const precision = currencyHelper.getCurrencyPrecision(this.currencyCode)
      const formattedValue = Number(fee || 0).toLocaleString(undefined, {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision
      })
      return `${this.currencySymbol} ${formattedValue}`.trim()
    },

    // 更新運費顯示
    updateShippingFee() {
      const shippingFee = this.getShippingFee()

      // 觸發運費更新事件，讓訂單摘要元件可以監聽
      this.$dispatch('shipping-fee-updated', { fee: shippingFee })
    },

    // 清除門市欄位
    clearStoreFields() {
      const fields = ['store_id', 'store_name', 'store_address']
      fields.forEach(field => {
        const input = document.querySelector(`input[name="order[order_recipient_attributes][${field}]"]`)
        if (input) input.value = ''
      })
    },

    openStoreSelector() {
      this.isLoadingStore = true

      if (this.shippingProvider === 'famimart') {
        this.openFamimartMap()
      } else if (this.shippingProvider === '7-11') {
        this.openDoubleServiceMap()
      } else {
        alert('請先選擇超商類型')
        this.isLoadingStore = false
      }
    },

    // 開啟全家電子地圖
    openFamimartMap() {
      // 檢查必要參數
      const famimartMapUrl = this.$refs.famimartMapUrl?.value
      const famimartCvsname = this.$refs.famimartCvsname?.value
      const famimartCallbackUrl = this.$refs.famimartCallbackUrl?.value
      const orderId = this.$refs.orderId?.value || '0'

      if (!famimartMapUrl || !famimartCvsname || !famimartCallbackUrl) {
        alert('系統設定錯誤，無法開啟全家門市選擇。請聯絡客服。')
        this.isLoadingStore = false
        return
      }

      // 組合全家電子地圖 URL（根據 PDF 文件第 7 頁規範）
      // 必要參數：cvsname, cvsid, cvstemp, exchange
      const params = new URLSearchParams({
        cvsname: famimartCvsname,
        cvsid: orderId,
        cvstemp: '',
        exchange: 'true'
      })

      // cvslink 需要單獨處理，因為它包含完整 URL
      const url = `${famimartMapUrl}?${params.toString()}&cvslink=${encodeURIComponent(famimartCallbackUrl)}`

      // 開啟新視窗
      const mapWindow = window.open(url, 'famimartMap', 'width=1024,height=768,resizable=yes,scrollbars=yes')

      if (!mapWindow) {
        alert('無法開啟門市選擇視窗，請檢查瀏覽器是否封鎖彈出視窗。')
        this.isLoadingStore = false
        return
      }

      // 監聽視窗關閉
      const checkClosed = setInterval(() => {
        if (mapWindow.closed) {
          clearInterval(checkClosed)
          this.isLoadingStore = false
        }
      }, 500)
    },

    // 開啟樂倍達電子地圖（7-11）
    openDoubleServiceMap() {
      // 檢查必要參數
      const storeMapUrl = this.$refs.storeMapUrl?.value
      const shippingNo = this.$refs.shippingNo?.value

      if (!storeMapUrl || !shippingNo) {
        alert('系統設定錯誤，無法開啟門市選擇。請聯絡客服。')
        this.isLoadingStore = false
        return
      }

      // 使用樂倍達電子地圖
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = storeMapUrl
      form.target = 'doubleServiceMap'
      form.acceptCharset = 'UTF-8'

      // 加入必要參數
      const params = {
        service_provider: '7-11',
        shipping_no: this.$refs.shippingNo.value,
        servicetype: '1', // 預設為付款取貨（之後會根據實際選擇調整）
        url: this.$refs.callbackUrl.value,
        tempvar: this.$refs.orderId.value || 'temp',
        display: this.isMobile() ? 'touch' : 'page',
        charset: 'UTF-8',
        hasoutside: '4' // 顯示本島門市
      }


      // 建立隱藏欄位
      Object.entries(params).forEach(([key, value]) => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = key
        input.value = value
        form.appendChild(input)
      })

      // 開啟新視窗
      const mapWindow = window.open('', 'doubleServiceMap', 'width=1024,height=768,resizable=yes,scrollbars=yes')

      // 提交表單
      document.body.appendChild(form)
      form.submit()
      document.body.removeChild(form)

      // 監聽視窗關閉和錯誤
      const checkClosed = setInterval(() => {
        if (mapWindow.closed) {
          clearInterval(checkClosed)
          this.isLoadingStore = false
        }

        // 嘗試檢查視窗內容（可能會因為跨域而失敗）
        try {
          if (mapWindow.document && mapWindow.document.title.includes('404') ||
            mapWindow.document.title.includes('Not Found')) {
            console.error('電子地圖載入失敗：可能是 shipping_no 無效或需要授權')
            alert('電子地圖載入失敗。請確認物流帳號設定是否正確。')
            mapWindow.close()
          }
        } catch (e) {
          // 跨域錯誤是正常的，忽略
        }
      }, 500)
    },

    // 處理門市選擇回傳
    handleStoreSelected(data) {
      // 更新門市資訊
      this.storeInfo = {
        id: data.storeid,
        name: data.storename,
        address: data.address
      }

      // 使用 Alpine 的 $nextTick 確保 DOM 更新後再設定值
      this.$nextTick(() => {
        // 更新隱藏欄位
        const storeIdInput = this.$el.querySelector('input[name="order[order_recipient_attributes][store_id]"]')
        const storeNameInput = this.$el.querySelector('input[name="order[order_recipient_attributes][store_name]"]')
        const storeAddressInput = this.$el.querySelector('input[name="order[order_recipient_attributes][store_address]"]')

        if (storeIdInput) {
          storeIdInput.value = data.storeid
        }
        if (storeNameInput) {
          storeNameInput.value = data.storename
        }
        if (storeAddressInput) {
          storeAddressInput.value = data.address
        }

        // 更新 CSRF token（如果需要）
        this.updateCSRFToken()

        // 觸發自定義事件通知其他元件
        this.$dispatch('store-selected', {
          storeid: data.storeid,
          storename: data.storename,
          address: data.address
        })
      })

      this.isLoadingStore = false
    },

    // 更新 CSRF token
    updateCSRFToken() {
      // 從 meta tag 取得最新的 CSRF token
      const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      if (token) {
        // 更新表單中的 CSRF token
        const csrfInput = document.querySelector('input[name="authenticity_token"]')
        if (csrfInput) {
          csrfInput.value = token
        }
      }
    },

    // 判斷是否為手機
    isMobile() {
      return window.innerWidth <= 768
    },

    useAddress() {
      const selected = document.querySelector(`input[name="selected_address"]:checked`);

      if (!selected) {
        alert(this.i18n.pleaseSelectAddress);
        return;
      }

      const element = selected.closest("label");
      const country = element.querySelector("p:first-child").innerText.trim();

      // 檢查國家是否在可配送清單中
      const countryValues = this.availableCountries.map(c => Array.isArray(c) ? c[1] : c.value || c);

      const methodSelect = document.querySelector('select[x-model="shippingMethod"]');
      const availableMethods = Array.from(methodSelect?.options || []).map(o => o.value).filter(v => v !== '');

      // 統一使用 $nextTick 處理所有地址類型
      if (country == "TW") {
        if (!availableMethods.includes('home_delivery')) {
          alert(this.i18n.domesticNotAvailable);
          return;
        }

        this.shippingMethod = 'home_delivery';
        this.handleShippingMethodChange();

        this.$nextTick(() => {
          window.AddressFormInstance.getAddress(selected);
          // 等地址完全帶入後再關閉彈窗
          setTimeout(() => {
            address_options.close();
          }, 300)
        });
      } else if (countryValues.includes(country)) {
        // 海外地址：檢查海外宅配是否在可用配送方式中
        if (!availableMethods.includes('overseas_delivery')) {
          alert(this.i18n.overseasNotAvailable);
          return;
        }

        this.$store.frontend.countryCode = country;
        this.shippingMethod = 'overseas_delivery';
        this.handleShippingMethodChange();

        this.$nextTick(() => {
          window.AddressFormInstance.getAddress(selected);
          // 等地址完全帶入後再關閉彈窗
          setTimeout(() => {
            address_options.close();
          }, 300)
        });
      } else {
        // 國家不在可配送清單中
        const message = this.i18n.countryNotAvailable.replace('{country}', country);
        alert(message);
      }
    }
  }
}

